import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { WouldYouRatherItem } from '../../../types/businessPlan';

interface WouldYouRatherProps {
  initialItems?: WouldYouRatherItem[];
  initialProfitCalc?: { costToMake: number; sellingPrice: number };
  onSaveItems: (items: WouldYouRatherItem[]) => void;
  onSaveProfitCalc: (calc: { costToMake: number; sellingPrice: number }) => void;
}

const CURRENCIES = [
  { symbol: '₹', code: 'INR' },
  { symbol: '$', code: 'USD' },
  { symbol: '€', code: 'EUR' },
  { symbol: '£', code: 'GBP' },
  { symbol: '¥', code: 'JPY' },
  { symbol: '₩', code: 'KRW' },
  { symbol: 'A$', code: 'AUD' },
  { symbol: 'C$', code: 'CAD' },
];

type Category = WouldYouRatherItem['category'];

export default function WouldYouRather({
  initialItems = [],
  initialProfitCalc,
  onSaveItems,
  onSaveProfitCalc,
}: WouldYouRatherProps) {
  const [items, setItems] = useState<WouldYouRatherItem[]>(initialItems);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [category, setCategory] = useState<Category | ''>('');
  const [costToMake, setCostToMake] = useState(
    initialProfitCalc?.costToMake?.toString() ?? '',
  );
  const [sellingPrice, setSellingPrice] = useState(
    initialProfitCalc?.sellingPrice?.toString() ?? '',
  );

  useEffect(() => {
    if (initialItems.length > 0) setItems(initialItems);
  }, [initialItems]);

  const canSave = name.trim() !== '' && price !== '' && Number(price) > 0 && category !== '';

  const handleSaveItem = () => {
    if (!canSave) return;
    const newItem: WouldYouRatherItem = {
      name: name.trim(),
      price: Number(price),
      category: category as Category,
      currency,
    };
    const updated = [...items, newItem];
    setItems(updated);
    onSaveItems(updated);
    setName('');
    setPrice('');
    setCategory('');
  };

  const handleDeleteItem = (idx: number) => {
    const updated = items.filter((_, i) => i !== idx);
    setItems(updated);
    onSaveItems(updated);
  };

  const handleProfitChange = (field: 'cost' | 'sell', val: string) => {
    const numVal = Number(val) || 0;
    if (field === 'cost') {
      setCostToMake(val);
      onSaveProfitCalc({ costToMake: numVal, sellingPrice: Number(sellingPrice) || 0 });
    } else {
      setSellingPrice(val);
      onSaveProfitCalc({ costToMake: Number(costToMake) || 0, sellingPrice: numVal });
    }
  };

  const margin =
    Number(sellingPrice) > 0 && Number(costToMake) >= 0
      ? Number(sellingPrice) - Number(costToMake)
      : null;

  const currSymbol = CURRENCIES.find((c) => c.code === currency)?.symbol ?? '₹';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto max-w-md"
    >
      <h2 className="text-center text-2xl font-bold text-gray-900">Would You Rather</h2>
      <p className="mt-1 text-center text-sm text-gray-500">
        List what your customer could buy instead at the same price
      </p>

      {/* Item counter */}
      <div className="mt-3 flex items-center justify-center gap-2 text-sm">
        <span className={items.length >= 5 ? 'text-green-600' : 'text-gray-400'}>
          {items.length >= 5 ? '✓' : '○'}
        </span>
        <span className="text-gray-600">
          Your list ({items.length}/5 minimum):
        </span>
      </div>

      {/* Saved items */}
      <AnimatePresence>
        {items.map((item, idx) => (
          <motion.div
            key={`${item.name}-${idx}`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 flex items-center gap-2 rounded-xl border-2 border-gray-200 bg-white px-4 py-2.5"
          >
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                item.category === 'Must-Have'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-cyan-100 text-cyan-700'
              }`}
            >
              {item.category}
            </span>
            <span className="flex-1 truncate text-sm font-medium text-gray-900">
              {item.name}
            </span>
            <span className="text-sm text-gray-500">
              {CURRENCIES.find((c) => c.code === item.currency)?.symbol}
              {item.price}
            </span>
            <button
              onClick={() => handleDeleteItem(idx)}
              className="ml-1 text-gray-300 hover:text-red-500"
            >
              🗑
            </button>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Add item form */}
      <div className="mt-4 rounded-2xl border-2 border-gray-200 bg-white p-4">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Movie ticket"
          className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-300 focus:border-brand-500 focus:outline-none"
        />

        <div className="mt-3 flex gap-2">
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="rounded-lg border-2 border-gray-200 px-2 py-2 text-sm focus:border-brand-500 focus:outline-none"
          >
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.symbol} {c.code}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="Price"
            min={0}
            className="flex-1 rounded-lg border-2 border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-300 focus:border-brand-500 focus:outline-none"
          />
        </div>

        <div className="mt-3 flex gap-2">
          {(['Must-Have', 'Nice-To-Have'] as const).map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`flex-1 rounded-lg border-2 px-3 py-2 text-sm font-medium transition-all ${
                category === cat
                  ? cat === 'Must-Have'
                    ? 'border-amber-400 bg-amber-50 text-amber-700'
                    : 'border-cyan-400 bg-cyan-50 text-cyan-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <button
          onClick={handleSaveItem}
          disabled={!canSave}
          className="mt-3 w-full rounded-xl bg-brand-500 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Add Item
        </button>
      </div>

      {/* Profit calculation */}
      <div className="mt-6 rounded-2xl border-2 border-green-200 bg-green-50 p-4">
        <h3 className="font-bold text-gray-900">Profit Calculator</h3>
        <div className="mt-3 flex gap-3">
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-500">Cost to make</label>
            <div className="mt-1 flex items-center gap-1">
              <span className="text-sm text-gray-400">{currSymbol}</span>
              <input
                type="number"
                value={costToMake}
                onChange={(e) => handleProfitChange('cost', e.target.value)}
                placeholder="0"
                min={0}
                className="w-full rounded-lg border-2 border-green-200 px-2 py-1.5 text-sm text-gray-900 focus:border-green-400 focus:outline-none"
              />
            </div>
          </div>
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-500">Selling price</label>
            <div className="mt-1 flex items-center gap-1">
              <span className="text-sm text-gray-400">{currSymbol}</span>
              <input
                type="number"
                value={sellingPrice}
                onChange={(e) => handleProfitChange('sell', e.target.value)}
                placeholder="0"
                min={0}
                className="w-full rounded-lg border-2 border-green-200 px-2 py-1.5 text-sm text-gray-900 focus:border-green-400 focus:outline-none"
              />
            </div>
          </div>
        </div>
        {margin !== null && (
          <p className={`mt-2 text-center text-sm font-bold ${margin >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            Profit per item: {currSymbol}{margin.toFixed(2)}
          </p>
        )}
      </div>
    </motion.div>
  );
}
